import { useEffect, useMemo } from "react";
import { Loader2 } from "lucide-react";
import ArtifactDescriptionEditor from "@/components/evidenceGathering/ArtifactDescriptionEditor";
import ArtifactTypeBadge from "@/components/evidenceGathering/ArtifactTypeBadge";
import ArtifactVersionBadge from "@/components/evidenceGathering/ArtifactVersionBadge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { parseCsv } from "@/lib/csv";
import { useArtifactFile } from "@/hooks/useArtifactFile";
import { isFromLatestUpload, isTableArtifact } from "@/lib/artifacts";
import type { Artifact } from "@/services/projectsApi";

interface ArtifactPreviewDialogProps {
  artifact: Artifact | null;
  projectId: string;
  latestUploadVersion?: number;
  onOpenChange: (open: boolean) => void;
}

const ArtifactPreviewDialog = ({
  artifact,
  projectId,
  onOpenChange,
  latestUploadVersion = 0,
}: ArtifactPreviewDialogProps) => {
  const { blobUrl, text, isLoading, error, fetchFile, clearFile } = useArtifactFile();
  const asTable = isTableArtifact(artifact);

  useEffect(() => {
    // An artifact whose extraction failed has no stored file, so fetching it
    // would only produce a 404 and a retry that can never succeed. The
    // recorded failure already says what happened.
    if (artifact?.blobName) fetchFile(projectId, artifact.uid, isTableArtifact(artifact), artifact.updatedAt);
    else clearFile();
    // Keyed on the uid, not the artifact object. The artifact is re-derived
    // from the list on every poll, so depending on the object would re-download
    // the file every few seconds.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artifact?.uid, projectId, fetchFile, clearFile]);

  const rows = useMemo(() => (text ? parseCsv(text) : []), [text]);

  return (
    <Dialog open={!!artifact} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[1320px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold">{artifact?.name}</DialogTitle>
          <DialogDescription asChild>
            <div className="flex items-center gap-2 pt-1">
              <ArtifactTypeBadge type={artifact?.type} />
              {artifact && (
                <ArtifactVersionBadge
                  uploadVersion={artifact.uploadVersion}
                  highlight={isFromLatestUpload(artifact, latestUploadVersion)}
                />
              )}
              <span className="text-xs text-muted-foreground">{artifact?.uid}</span>
              {artifact?.section && (
                <span className="text-xs text-muted-foreground">· {artifact.section}</span>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>

        {isLoading && !error && (
          <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading artefact…
          </div>
        )}

        {/* A recorded failure is a statement about the artifact, not about this
            request, so it never offers a retry and never hides content that
            does load — the record can outlive the problem it describes. */}
        {artifact?.error && !isLoading && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3">
            <p className="text-sm text-destructive">{artifact.error}</p>
          </div>
        )}

        {error && (
          <div className="py-12 text-center">
            <p className="text-sm text-destructive">Could not load this artefact.</p>
            {/* The retry stays in place rather than swapping to the page
                spinner. A failure that returns in milliseconds would otherwise
                flash and re-render identically, leaving the button looking
                dead when it had in fact run. */}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-3 gap-1.5"
              disabled={isLoading}
              onClick={() => artifact?.blobName && fetchFile(projectId, artifact.uid, asTable, artifact.updatedAt)}
            >
              {isLoading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {isLoading ? "Retrying…" : "Try again"}
            </Button>
          </div>
        )}

        {!isLoading && !error && asTable && rows.length > 0 && (
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {rows[0].map((cell, i) => (
                    <TableHead key={i} className="h-auto py-2">
                      {cell}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.slice(1).map((row, r) => (
                  <TableRow key={r}>
                    {row.map((cell, c) => (
                      <TableCell key={c} className="py-1.5 text-sm">{cell}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {!isLoading && !error && !asTable && blobUrl && (
          <div className="flex justify-center bg-muted/30 rounded-md p-4">
            <img src={blobUrl} alt={artifact?.name ?? "Artefact"} className="max-w-full h-auto" />
          </div>
        )}

        {artifact && !isLoading && (
          <div className="rounded-md border bg-muted/20 p-3">
            <ArtifactDescriptionEditor artifact={artifact} projectId={projectId} />
          </div>
        )}

      </DialogContent>
    </Dialog>
  );
};

export default ArtifactPreviewDialog;
